import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeo, useJsonLd, SITE_URL } from '../lib/seo';
import {
  ArrowRight,
  Shield,
  Truck,
  Crosshair,
  RectangleHorizontal,
  Wrench,
  Clock,
  CheckCircle2,
  Calendar,
  BadgeCheck,
} from 'lucide-react';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import QuoteRequestModal from '../components/site/QuoteRequestModal';
import { BLOG_POSTS as ALL_BLOG_POSTS } from '../data/blog';

// Show only the 4 most recent posts on the Services page teaser.
const BLOG_POSTS = ALL_BLOG_POSTS.slice(0, 4);

const IMG = {
  process: 'https://static.prod-images.emergentagent.com/jobs/2f54c576-01e2-4664-be6d-66ca282e4aae/images/abd2205d31b839d38225f0a4a27d529e16fd3bfed700f33fcda6306557e9a232.jpeg',
  windshield: 'https://static.prod-images.emergentagent.com/jobs/2f54c576-01e2-4664-be6d-66ca282e4aae/images/95b357476ab0d5a057ffd3babf1760adb50ca339cf300e9520cb936c8a34dfbb.jpeg',
  rockChip: 'https://static.prod-images.emergentagent.com/jobs/2f54c576-01e2-4664-be6d-66ca282e4aae/images/469d8be22592634b1869b7efa39e66620bd29ec7f7368f208725960671a2b5b0.jpeg',
  adas: 'https://static.prod-images.emergentagent.com/jobs/2f54c576-01e2-4664-be6d-66ca282e4aae/images/631e077f209b205c012dcaf2ccd802d3a68d9c432002175c6a7309bf34de6770.jpeg',
  mobile: 'https://static.prod-images.emergentagent.com/jobs/2f54c576-01e2-4664-be6d-66ca282e4aae/images/3616a9288e90c82b765e99690effd133eb0d9c5bb1fb17e3b5a75c2d197b6837.jpeg',
  insurance: 'https://static.prod-images.emergentagent.com/jobs/2f54c576-01e2-4664-be6d-66ca282e4aae/images/6e79e697170687320cd00a20712cbdd922f222403dfa7917a3611e32f0688432.jpeg',
};

const SERVICES = [
  {
    id: 'windshield-replacement',
    icon: RectangleHorizontal,
    title: 'Windshield Replacement',
    heading: 'Windshield replacement in Ottawa & Orléans',
    tagline: 'Quality glass, expert installation, safe drive-away.',
    desc:
      'Professional windshield replacement in Ottawa and Orléans for every make and model. We fit OEM or OEE glass, bond it with premium urethane and respect the manufacturer\u2019s safe drive-away time, so your vehicle leaves our care as strong and quiet as it was from the factory.',
    bullets: [
      'OEM and OEE glass options for all makes and models',
      'Urethane bonding that meets AGRSS safety standards',
      'Lifetime workmanship warranty against leaks and defects',
    ],
    img: IMG.windshield,
    alt: 'A+ Auto Glass technicians setting a new windshield on an SUV in Ottawa',
  },
  {
    id: 'rock-chip-repair',
    icon: Wrench,
    title: 'Rock Chip Repair',
    heading: 'Stone chip repair before it becomes a crack',
    tagline: 'Repaired in about 30 minutes — deductible often waived.',
    desc:
      'Ottawa winters and highway gravel are hard on glass. A single stone chip can run across your whole windshield in a matter of days. Our resin injection repair fills the break, restores structural strength and keeps your original factory seal — and many Ontario insurers waive the deductible on chip repairs, so it often costs you nothing.',
    bullets: [
      'Most chips repaired in 30 minutes or less',
      'Restores the structural strength of the glass',
      'Deductible often waived for chip repairs — check your policy',
    ],
    img: IMG.rockChip,
    alt: 'Close-up of a resin injection tool repairing a stone chip in a windshield',
  },
  {
    id: 'adas-calibration',
    icon: Crosshair,
    title: 'ADAS Calibration',
    heading: 'ADAS camera calibration after glass replacement',
    tagline: 'Safety systems recalibrated back to factory spec.',
    desc:
      'Professional ADAS camera calibration in Ottawa and Orléans. If your vehicle has lane-keep assist, adaptive cruise control or automatic emergency braking, the forward-facing camera must be recalibrated after a windshield replacement. We complete static and dynamic calibration in-house and hand you the scan-tool report.',
    bullets: [
      'Static and dynamic calibration performed in-house',
      'Manufacturer scan-tool documentation provided',
      'Included in most windshield replacement quotes',
    ],
    img: IMG.adas,
    alt: 'ADAS calibration target set up in front of a vehicle in an Ottawa auto glass shop',
  },
  {
    id: 'mobile-service',
    icon: Truck,
    title: 'Mobile Service',
    heading: 'Mobile auto glass service across the Ottawa area',
    tagline: 'We come to your driveway, office or job site.',
    desc:
      'Convenient mobile windshield replacement and chip repair in Ottawa, Orléans, Kanata, Gatineau, Nepean, Barrhaven and the surrounding communities. Our fully equipped mobile units carry everything needed to complete the job properly at your home or workplace — usually the same or next day.',
    bullets: [
      'Same-day and next-day appointments available',
      'Fully equipped mobile units and certified technicians',
      'No extra travel fee within the Ottawa area',
    ],
    img: IMG.mobile,
    alt: 'A+ Auto Glass mobile service van parked in an Ottawa driveway',
  },
  {
    id: 'insurance-claims',
    icon: Shield,
    title: 'Insurance Claims',
    heading: 'Insurance claims handled from start to finish',
    tagline: 'We do the paperwork, you skip the hassle.',
    desc:
      'We are approved by every major Canadian insurer and bill them directly. Send us your policy details and we will confirm what your policy covers, open the claim and keep you updated until the job is done. Glass claims fall under your comprehensive coverage, so you are responsible for your comprehensive deductible unless your policy includes a glass endorsement that waives it.',
    bullets: [
      'Direct billing to all major insurance providers',
      'Your comprehensive deductible applies unless your policy waives it for glass',
      'Claim opened, documented and followed up by our team',
    ],
    img: IMG.insurance,
    alt: 'Service advisor reviewing an auto glass insurance claim with a customer',
  },
];

const TRUST = [
  { icon: Clock, label: 'Same / next-day service' },
  { icon: BadgeCheck, label: 'Lifetime workmanship warranty' },
  { icon: Shield, label: 'Approved by all major insurers' },
  { icon: Crosshair, label: 'In-house ADAS calibration' },
];

const FAQS = [
  {
    q: 'How much does a windshield replacement cost in Ottawa?',
    a: 'Pricing depends on your vehicle, the type of glass and whether ADAS calibration is required. Most replacements for common vehicles fall between $350 and $700 before insurance — send us a quote request and we will confirm an exact price, usually the same business day.',
  },
  {
    q: 'How long does the job take?',
    a: 'A windshield replacement takes about 60 to 90 minutes, plus roughly an hour of safe drive-away time for the adhesive to cure. A stone chip repair is usually finished in under 30 minutes.',
  },
  {
    q: 'Will my insurance cover it?',
    a: 'Glass damage is covered under the comprehensive section of most Ontario policies. Your comprehensive deductible applies unless you carry a glass endorsement that waives it — many insurers also waive the deductible for chip repairs. We bill all major insurers directly and handle the claim paperwork for you.',
  },
  {
    q: 'Do I need ADAS calibration after a windshield replacement?',
    a: 'If your vehicle has lane-keep assist, adaptive cruise control or automatic emergency braking, yes. The forward-facing camera sits on the windshield and must be recalibrated to factory specification so the systems react correctly.',
  },
  {
    q: 'Which areas do you serve?',
    a: 'Ottawa, Orléans, Kanata, Nepean, Barrhaven, Gloucester, Gatineau and the surrounding communities. Mobile service is available across the region at no extra travel charge.',
  },
  {
    q: 'Can a chip be repaired instead of replacing the windshield?',
    a: 'Usually, if the chip is smaller than a loonie and outside the driver\u2019s direct line of sight. Repairing early is faster, cheaper and keeps your original factory seal intact.',
  },
];

// ---------------- Reveal-on-scroll ----------------
function useOnScreen(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return visible;
}

function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const visible = useOnScreen(ref);
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

// ---------------- SEO helpers ----------------
function useBlogJsonLd(posts) {
  useEffect(() => {
    const data = {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'A+ Auto Glass Insights',
      description:
        'Expert guides on windshield replacement, rock chip repair, ADAS calibration and auto glass insurance in Ottawa.',
      blogPost: posts.map((p) => ({
        '@type': 'BlogPosting',
        headline: p.title,
        image: p.img,
        datePublished: p.date,
        author: { '@type': 'Organization', name: p.author },
        articleSection: p.category,
        description: p.excerpt,
        mainEntityOfPage: `${window.location.origin}/blog/${p.slug}`,
      })),
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-testid', 'services-blog-jsonld');
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [posts]);
}

const servicesSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Auto glass services in Ottawa & Orléans',
  itemListElement: SERVICES.map((s, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'Service',
      name: s.title,
      description: s.desc,
      serviceType: s.title,
      url: `${SITE_URL}/services#${s.id}`,
      provider: { '@type': 'AutoRepair', name: 'A+ Auto Glass', telephone: '+1-343-558-8820' },
      areaServed: ['Ottawa', 'Orléans', 'Kanata', 'Gatineau', 'Nepean', 'Barrhaven'],
    },
  })),
};

const servicesFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

// ---------------- Page ----------------
export default function Services() {
  const [quoteOpen, setQuoteOpen] = useState(false);

  useSeo({
    title: 'Auto Glass Services in Ottawa & Orléans — Windshield Replacement, Rock Chip Repair, ADAS Calibration | A+ Auto Glass',
    description:
      'Windshield replacement, rock chip repair, ADAS camera calibration, mobile auto glass service and direct insurance billing in Ottawa, Orléans, Kanata, Nepean and Gatineau. Same/next-day service, lifetime workmanship warranty. Call 343-558-8820.',
    path: '/services',
  });
  useJsonLd('ld-services', servicesSchema);
  useJsonLd('ld-services-faq', servicesFaqSchema);
  useBlogJsonLd(BLOG_POSTS);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const handler = () => setQuoteOpen(true);
    window.addEventListener('open-quote-modal', handler);
    return () => window.removeEventListener('open-quote-modal', handler);
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#111827]" data-testid="services-page">
      <Navbar />

      {/* HERO */}
      <section className="relative bg-[#0a1220] text-white overflow-hidden">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-[#0D5BD7]/20 to-transparent pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-12 md:py-14">
          <h1 className="font-black leading-[1.05] tracking-[-0.015em] text-[32px] md:text-[42px] lg:text-[48px] max-w-[680px]">
            Services Offered
          </h1>
          <p className="mt-4 text-[15px] md:text-[16.5px] text-white/85 max-w-[600px] leading-[1.65]">
            Windshield replacement, stone chip repair and ADAS camera calibration by certified local
            technicians — in our Orléans shop or at your door.
          </p>

          <ul className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
            {TRUST.map((t) => (
              <li key={t.label} className="flex items-center gap-2.5 text-[13.5px] text-white/80">
                <t.icon className="w-4 h-4 text-[#7fb0ff] shrink-0" strokeWidth={1.8} />
                {t.label}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* DETAILED SECTIONS */}
      <section className="bg-[#F8F9FB]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-24 space-y-16 md:space-y-24">
          {SERVICES.map((s, i) => {
            const reversed = i % 2 === 1;
            return (
              <Reveal key={s.id}>
                <article
                  id={s.id}
                  data-testid={`service-detail-${s.id}`}
                  className="scroll-mt-32 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center"
                >
                  <div className={`lg:col-span-6 ${reversed ? 'lg:order-2' : ''}`}>
                    <div className="relative rounded-2xl overflow-hidden shadow-[0_18px_50px_rgba(15,23,42,0.12)]">
                      <img
                        src={s.img}
                        alt={s.alt}
                        className="w-full h-full object-cover aspect-[16/11]"
                        loading="lazy"
                      />
                      <span className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-4 py-2 text-[11px] font-bold tracking-[0.18em] text-[#0D5BD7] shadow-sm">
                        <s.icon className="w-4 h-4" strokeWidth={2} />
                        {String(i + 1).padStart(2, '0')} / {String(SERVICES.length).padStart(2, '0')}
                      </span>
                    </div>
                  </div>
                  <div className={`lg:col-span-6 ${reversed ? 'lg:order-1' : ''}`}>
                    <div className="text-[12px] font-bold tracking-[0.26em] text-[#0D5BD7]">
                      {s.title.toUpperCase()}
                    </div>
                    <h2 className="mt-4 text-[28px] md:text-[38px] font-black text-[#111827] leading-[1.1] tracking-[-0.01em]">
                      {s.heading}
                    </h2>
                    <p className="mt-5 text-[16px] md:text-[17px] text-[#5F6773] leading-[1.8]">{s.desc}</p>
                    <ul className="mt-7 space-y-3">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-[15px] text-[#111827] leading-[1.6]">
                          <CheckCircle2 className="w-5 h-5 text-[#0D5BD7] shrink-0 mt-0.5" strokeWidth={1.8} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3">
                      <button
                        onClick={() => setQuoteOpen(true)}
                        data-testid={`service-detail-quote-${s.id}`}
                        className="group inline-flex items-center justify-center min-h-[52px] px-7 rounded-full bg-[#0D5BD7] hover:bg-[#0a4bb5] text-white text-[11.5px] font-bold tracking-[0.22em] transition-all shadow-sm hover:shadow-md hover:-translate-y-[1px] active:scale-[0.98]"
                      >
                        GET A FREE QUOTE
                        <ArrowRight className="w-3.5 h-3.5 ml-3 transition-transform group-hover:translate-x-1" />
                      </button>
                      <a
                        href="tel:3435588820"
                        className="inline-flex items-center justify-center min-h-[52px] px-7 rounded-full border-2 border-[#0D5BD7] text-[#0D5BD7] hover:bg-[#0D5BD7] hover:text-white text-[11.5px] font-bold tracking-[0.22em] transition-colors active:scale-[0.98]"
                      >
                        CALL 343.558.8820
                      </a>
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F8F9FB] py-20 md:py-24" data-testid="services-faq">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-[720px]">
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">COMMON QUESTIONS</div>
            <h2 className="mt-4 text-[30px] md:text-[42px] font-black text-[#111827] tracking-[-0.01em] leading-[1.08]">
              Auto glass questions, answered.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-x-10">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 50}>
                <div
                  data-testid={`services-faq-item-${i}`}
                  className="h-full rounded-2xl bg-white border border-slate-100 p-7 md:p-8 shadow-[0_2px_16px_rgba(15,23,42,0.05)]"
                >
                  <h3 className="text-[17px] font-black text-[#111827] leading-snug tracking-tight">{f.q}</h3>
                  <p className="mt-3 text-[15px] text-[#5F6773] leading-[1.75]">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="bg-[#111827] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 md:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#4a8cff]">READY TO GO?</div>
            <h2 className="mt-3 text-[28px] md:text-[36px] font-black leading-[1.1] tracking-[-0.01em]">
              Get your quote in minutes.
            </h2>
            <p className="mt-3 text-[14.5px] text-[#c3c9d4] leading-[1.7] max-w-xl">
              Tell us about your vehicle and one of our team members will get back to you shortly to
              confirm your quote — usually within the same business day.
            </p>
          </div>
          <button
            onClick={() => setQuoteOpen(true)}
            data-testid="services-cta-quote-btn"
            className="group shrink-0 inline-flex items-center justify-center w-full md:w-auto h-14 px-8 rounded-full bg-[#0D5BD7] hover:bg-[#0a4bb5] text-white text-[12px] font-bold tracking-[0.22em] transition-all shadow-md hover:shadow-lg hover:-translate-y-[1px] active:scale-[0.98]"
          >
            REQUEST A QUOTE
            <ArrowRight className="w-4 h-4 ml-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      {/* Blog / Insights (above footer, for SEO) */}
      <section
        id="insights"
        data-testid="services-blog"
        className="bg-white py-20 md:py-24"
        aria-label="A+ Auto Glass insights and articles"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">INSIGHTS & GUIDES</div>
              <h2 className="mt-3 text-[30px] md:text-[42px] font-black text-[#111827] tracking-[-0.01em] max-w-2xl leading-[1.05]">
                From Our Auto Glass Experts.
              </h2>
              <p className="mt-4 text-[15px] text-[#5F6773] leading-[1.7] max-w-2xl">
                Practical guides for Ontario drivers on windshield damage, ADAS safety systems, and how to use your
                insurance for glass repair.
              </p>
            </div>
            <Link
              to="/blog"
              data-testid="services-view-all-articles"
              className="group shrink-0 self-start md:self-end inline-flex items-center gap-2 h-11 px-6 rounded-full border-2 border-[#0D5BD7] text-[#0D5BD7] hover:bg-[#0D5BD7] hover:text-white text-[11.5px] font-bold tracking-[0.22em] transition-colors"
            >
              VIEW ALL ARTICLES
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {BLOG_POSTS.map((p, i) => (
              <Reveal key={p.id} delay={i * 80}>
                <article
                  data-testid={`blog-post-${p.id}`}
                  className="group h-full flex flex-col bg-white rounded-2xl border border-slate-100 shadow-[0_2px_16px_rgba(15,23,42,0.05)] hover:shadow-[0_18px_40px_rgba(13,91,215,0.1)] hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                >
                  <Link
                    to={`/blog/${p.slug}`}
                    aria-label={p.title}
                    className="block aspect-[16/9] overflow-hidden bg-slate-100"
                  >
                    <img
                      src={p.img}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                      loading="lazy"
                    />
                  </Link>
                  <div className="p-7 md:p-8 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-[11px] font-bold tracking-[0.2em] text-[#0D5BD7]">
                      <span>{p.category.toUpperCase()}</span>
                      <span className="w-1 h-1 rounded-full bg-slate-300" />
                      <span className="inline-flex items-center gap-1 text-slate-500">
                        <Calendar className="w-3 h-3" /> {p.date}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-slate-300" />
                      <span className="inline-flex items-center gap-1 text-slate-500">
                        <Clock className="w-3 h-3" /> {p.readTime}
                      </span>
                    </div>
                    <h3 className="mt-4 text-[20px] md:text-[22px] font-black text-[#111827] tracking-tight leading-[1.2]">
                      <Link to={`/blog/${p.slug}`} className="hover:text-[#0D5BD7] transition-colors">
                        {p.title}
                      </Link>
                    </h3>
                    <p className="mt-3 text-[14.5px] text-[#5F6773] leading-[1.7] flex-1">{p.excerpt}</p>
                    <Link
                      to={`/blog/${p.slug}`}
                      className="mt-5 inline-flex items-center text-[11px] font-bold tracking-[0.22em] text-[#0D5BD7] hover:underline"
                    >
                      READ ARTICLE
                      <ArrowRight className="w-3.5 h-3.5 ml-2 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <div className="mt-12 text-center">
            <span className="text-[13px] text-[#5F6773]">
              Have a question in the meantime?{' '}
              <Link to="/contact" className="font-semibold text-[#0D5BD7] hover:underline">
                Contact our team
              </Link>
              .
            </span>
          </div>
        </div>
      </section>

      <Footer />
      <QuoteRequestModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </div>
  );
}
